import { create } from 'zustand';
import { apiGet, apiPost, apiDelete } from '../api/apiClient';

export interface AppNotification {
  id: string;
  userId: string;
  type: 'LIKE' | 'COMMENT' | 'MESSAGE' | 'FUNDING' | 'POST_IDEA' | 'INTEREST';
  sourceId?: string;
  sourceUsername?: string;
  ideaId?: string;
  timestamp: string;
  read: boolean;
}

interface NotificationState {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => void;
  clearAllNotifications: () => Promise<void>;
  subscribeWebPush: () => Promise<boolean>;
  addNotification: (notification: AppNotification) => void;
}

const PUBLIC_VAPID_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string;

// Utility to convert base64 to Uint8Array for VAPID
function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const useNotificationStore = create<NotificationState>((set, get) => {
  // Listen for socket events dispatched from socketStore
  window.addEventListener('new-notification', ((e: CustomEvent<AppNotification>) => {
    get().addNotification(e.detail);
  }) as EventListener);

  return {
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null,

    addNotification: (notification) => {
      set((state) => ({
        notifications: [notification, ...state.notifications],
        unreadCount: state.unreadCount + (notification.read ? 0 : 1)
      }));
    },

    fetchNotifications: async () => {
      set({ loading: true, error: null });
      try {
        const response = await apiGet<{ events: AppNotification[] }>('/notifications/events');
        if (response.data) {
          const events = response.data.events;
          const unreadCount = events.filter(e => !e.read).length;
          set({ notifications: events, unreadCount, loading: false });
        }
      } catch (err: any) {
        set({ error: err.message || 'Failed to fetch notifications', loading: false });
      }
    },

    markAsRead: (id: string) => {
      set((state) => ({
        notifications: state.notifications.map(n => 
          n.id === id ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      }));
    },

    clearAllNotifications: async () => {
      try {
        await apiDelete('/notifications/events');
        set({ notifications: [], unreadCount: 0 });
      } catch (err) {
        console.error('Failed to clear notifications:', err);
      }
    },

    subscribeWebPush: async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        return false;
      }

      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY)
        });

        await apiPost('/notifications/subscribe/web', { subscription });
        return true;
      } catch (err) {
        console.error('Failed to subscribe to web push:', err);
        return false;
      }
    }
  };
});
